import { useContext, useDeferredValue } from 'react';

import type { LooseRouteParams } from '@routes/types';
import type { UseDeferredStateOptions } from '@hooks/types';

import type { RouteContextType } from './router.context';
import {
  LocationContext,
  NavigatorContext,
  RouteContext,
  RouterContext,
} from './router.context';

export type UseRouteContextOptions = UseDeferredStateOptions;

/**
 * Returns a deferred state of the resolved route context object for the current
 * location.
 *
 * If you need the eager state, pass `deferred: false` to the options.
 */
export const useRouteContext = <
  Params extends LooseRouteParams = LooseRouteParams,
>({ deferred = true }: UseRouteContextOptions = {}) => {
  const context = useContext(RouteContext) as RouteContextType<Params>;
  const deferredContext = useDeferredValue(context);

  return deferred ? deferredContext : context;
};

export const useRouter = () => useContext(RouterContext);

export const useNavigator = () => useContext(NavigatorContext);

export const useLocation = () => useContext(LocationContext);
